import Cookies from 'js-cookie';
import base64 from 'base-64';

import {browser} from '$app/environment';
import {goto} from '$app/navigation';
import {get} from 'svelte/store';
import {redirect} from '@sveltejs/kit';

import {query, previous_query, categories, dossiers, start_date, start_date_string, end_date, end_date_string, clicked_date, clicked_category, current_dossier_id, dossier_accordion_active, url_search_params_string} from '$lib/store.js';
import {app_url, staging_basic_auth} from '$lib/svelte_1848_settings.js';

// Somehow using the relative URL is required for the SvelteKit provided
// 'fetch' to work on staging/beta (using the absolute URL results in fetch
// running both during SSR and CSR which is wasteful), but the relative URL
// doesn't work on dev; so use the absolute URL when on dev
let base = '/api/v1';
if (app_url === 'https://localdev-app.1848.nl/') {
    base = `${app_url}api/v1`;
}

function init_call(load_data) {
    // Renew this cookie if logged in (this is needed as Django will also
    // refresh the sessionid cookie and we want both cookies to expire at the
    // same time); only run client side, otherwise it is run twice unnecessarily
    if (Cookies.get('authenticatedAccount')) {
        Cookies.set('authenticatedAccount', Cookies.get('authenticatedAccount'), {expires: 366, sameSite: 'lax', secure: 'true'});
    }

    let headers = {
        'Content-Type': 'application/json',
    };

    // If the calls are coming from SvelteKit's server side load function
    // then retrieve the cookies from the load data (i.e. +layout.server.js) and
    // set the X-CSRFToken header
    if (load_data) {
        headers['X-CSRFToken'] = load_data.csrftoken;
    } else {
        headers['X-CSRFToken'] = Cookies.get('csrftoken');
    }

    if (staging_basic_auth) {
        headers['Authorization'] = 'Basic ' + base64.encode(staging_basic_auth);
    }

    return headers;
}

// GET API fetch
export async function get_call(request, load_fetch, load_data) {
    // If available use SvelteKit's load function fetch
    if (!load_fetch) {
        load_fetch = fetch;
    }

    let headers = init_call(load_data);

    let response = await load_fetch(`${base}${request.endpoint}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: headers
    });

    // If the response is 403 because the sessionid is not valid, logout
    if (response.status === 403) {
        if (browser) {
            goto(`${app_url}logout`, {invalidateAll: true});
        } else {
            throw redirect(302, `${app_url}logout`);
        }
    }

    // Check if the response contains JSON, otherwise return the response object
    try {
        let response_json = await response.json();
        // Special case for the 'valideer' page, which returns an empty JSON,
        // and we need to response object to check the status
        if (response_json && Object.keys(response_json).length === 0) {
            throw 'Empty JSON';
        } else {
            return response_json;
        }
    } catch(err) {
        return response;
    }
}

// POST API fetch
export async function post_call(request, load_fetch, load_data) {
    // If available use SvelteKit's load function fetch
    if (!load_fetch) {
        load_fetch = fetch;
    }

    let headers = init_call(load_data);

    // Needed to prevent this error when running server side:
    // 'CSRF Failed: Referer checking failed - no Referer'
    if (!browser) {
        headers['Referer'] = app_url;
    }

    let response = await load_fetch(`${base}${request.endpoint}`, {
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify(request.body || {}),
        headers: headers
    });

    // If the response is 403 because the sessionid is not valid, logout
    if (response.status === 403) {
        if (browser) {
            goto(`${app_url}logout`, {invalidateAll: true});
        } else {
            throw redirect(302, `${app_url}logout`);
        }
    }

    // Check if the response contains JSON, otherwise return the response object
    try {
        let response_json = await response.json();
        return response_json;
    } catch(err) {
        return response;
    }
}

// PATCH API fetch
export async function patch_call(request, load_fetch, load_data) {
    // If available use SvelteKit's load function fetch
    if (!load_fetch) {
        load_fetch = fetch;
    }

    let headers = init_call(load_data);

    return await load_fetch(`${base}${request.endpoint}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        body: JSON.stringify(request.body || {}),
        headers: headers
    }).then((r) => r.json());
}

// DELETE API fetch
export async function delete_call(request, load_fetch, load_data) {
    // If available use SvelteKit's load function fetch
    if (!load_fetch) {
        load_fetch = fetch;
    }

    let headers = init_call(load_data);

    return await load_fetch(`${base}${request.endpoint}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: headers
    });
}

// If at least one filter is checked the filter accordion in the dossier modal
// should be open, otherwise keep it closed
export function check_accordion_active(categories) {
    let selected_facets = Object.keys(categories).filter(function(category) {
        return categories[category];
    })
    if (selected_facets.length) {
        dossier_accordion_active.set('show');
    } else {
        dossier_accordion_active.set('');
    }
}

// Top level categories used by the old 1848, mapped with their subcategories
// that we use in the new 1848
const top_categories = {
    'Parlementaire documenten': ['Documenten Tweede Kamer', 'Documenten Eerste Kamer', 'Advies Raad van State', 'Staatsblad'],
    'Europees Parlement': ['Documenten Europese Commissie', 'Aangenomen teksten', 'Debatten'],
    'Internetconsultaties': ['Consultaties'],
    'Rijksoverheid':  ['Ministerraad'],
    'Media': ['Persberichten'],
    '1848': []
};

// List of categories which have subcategories
export const nested_categories = [
    'Parlementaire documenten|Documenten Eerste Kamer',
    'Parlementaire documenten|Documenten Tweede Kamer',
    'Europees Parlement|Documenten Europese Commissie'
];

// TODO the code below should be split into 4 functions because the 1st and 3rd
// part are only needed by init_edit_dossier_data() and this results in a ugly
// check for 'wijzig-dossier'.
// Logic to deal with (un)checking nested categories
export function process_nested_categories(categories) {
    // Let _event be an empty string or if there is an event, use that
    let _event = '';
    if (typeof event !== 'undefined') {
        _event = event;
    }

    // If a dossier is selected, process the top level nested categories which
    // might be used by dossiers created with the old 1848 interface
    for (const [top_cat, nested_cats] of Object.entries(top_categories)) {
        // If a top level category is set to true
        if (categories[top_cat]) {
            // Set the top level category to false (as we don't use them anymore)
            categories[top_cat] = false;
            // Set its nested categories to true
            nested_cats.forEach(nested_cat => {
                categories[top_cat + '|' + nested_cat] = true;
            });
        }
    }

    // Process changes for categories with subcategories
    nested_categories.forEach(nested_category => {
        // If the user unchecked a child of a nested_category facet, then
        // uncheck nested_category
        if (_event?.target?.id?.includes(nested_category.replace(/ /g, '_') + '|') && !categories[_event?.target?.id?.replace(/:.+$/, '').replace(/_/g, ' ')] && categories[nested_category]) {
            categories[nested_category] = false;
        }

        // If the user checked or (wijzig) dossier contains a nested_category facet,
        // then check all its children
        if ((_event?.target?.id?.replace(/:.+$/, '').endsWith(nested_category.replace(/ /g, '_')) || get(current_dossier_id) || _event?.target?.id === 'wijzig-dossier') && categories[nested_category]) {
            Object.keys(categories).forEach(category => {
                if (category.startsWith(nested_category)) {
                    categories[category] = true;
                }
            });
        }

        // If the user unchecked a nested_category facet, then uncheck all
        // its children
        if (_event?.target?.id?.replace(/:.+$/, '').endsWith(nested_category.replace(/ /g, '_')) && !categories[nested_category]) {
            Object.keys(categories).forEach(category => {
                if (category.startsWith(nested_category)) {
                    categories[category] = false;
                }
            });
        }
    });
}

// Unchecks all categories
export function uncheck_categories(categories) {
    categories.update(categories => {
        Object.keys(categories).forEach(category => categories[category] = false);
        return categories;
    });
}

// Check if two passwords are equal
export function check_password_equality(password, confirm_password) {
    if (password.value === confirm_password.value) {
      confirm_password.setCustomValidity('');
    } else {
      confirm_password.setCustomValidity('Wachtwoorden zijn niet hetzelfde');
    }
}

export async function process_cookies(load_fetch, cookies) {
    // This always sets a csrftoken if the client's browser doesn't have one.
    // It is currently not checked by DRF when the user isn't logged in so the
    // code is commented and might be used later. Currently once the user logs
    // in a csrftoken is set and required by DRF.
    /*
    if (!cookies.get('csrftoken')) {
        let response = await load_fetch(`${base}/get-csrf`);

        if (response.ok) {
            cookies.set(
                'csrftoken',
                response.headers.get('set-cookie').match(/csrftoken=([^;]*);/)[1],
                {
                    maxAge: 31449600,
                }
            )
        };
    }
    */

    // We need to manually set(/forward) the sessionid cookie for server side
    // calls (we need to do this here, because this cookie is HttpOnly and thus
    // can't be altered client side)
    if (cookies.get('sessionid')) {
        cookies.set(
            'sessionid',
            cookies.get('sessionid'),
            {
                // 366 days + 1 second because the server side request fires
                // before the authenticatedAccount cookie is set client side
                // (and 1 second seems to be the average difference)
                maxAge: 31622401,
                sameSite: 'lax',
                secure: 'true',
                path: '/'
            }
        );
    }

    // Forward these cookies so +page.js load functions have access to them
    // when running server side
    return {
        csrftoken: cookies.get('csrftoken'),
        authenticated_account: cookies.get('authenticatedAccount'),
        sessionid: cookies.get('sessionid'),
    };
}

// Check if the user is logged based on if authenticatedAccount and sessionid
// cookies are present. This is only checked server side because sessionid is
// HttpOnly, so get the cookies from 'data' (i.e. +page/layout.server.js)
export function check_logged_in(data) {
    let logged_in = undefined;

    if (data.authenticated_account || data.sessionid) {
        if (data.authenticated_account && data.sessionid) {
            try {
                logged_in = JSON.parse(data.authenticated_account);
            // If the JSON of the cookie is invalid, then logout the user
            } catch (err) {
                throw redirect(302, `${app_url}logout`);
            }
        // If only one of both cookies is present, then logout the user
        } else {
            throw redirect(302, `${app_url}logout`);
        }
    }

    return logged_in;
}

// Get the values that define the requested state; called by
// /app/(agenda/)+page.js load functions to which the state values are returned
// which are used to make API requests, but also sets a few values as stores
// for easy access at other places
export function get_requested_state(url, load_data) {
    // Set the URL parameter string
    url_search_params_string.set(url.search);

    let _query = '';
    let dossier_id = '';
    current_dossier_id.set('');
    // Check load_data in case of server side and Cookies in case of client
    // side for availability of an authenticated account
    if (load_data?.authenticated_account || Cookies.get('authenticatedAccount')) {
        dossier_id = 'all';
        if (url.searchParams.has('dossier')) {
            dossier_id = url.searchParams.get('dossier', '');
        }
        current_dossier_id.set(dossier_id);

        if (dossier_id && dossier_id !== 'all') {
            get(dossiers).forEach(dossier => {
                if (dossier.id == get(current_dossier_id)) {
                    _query = dossier.query_data.query;

                    // TODO could we already set the categories here instead of
                    // calling process_dossier()?
                    //let temp_categories = get(categories);
                    //dossier.notification_item_category_filter.forEach(category => {
                    //    temp_categories[category] = true;
                    //});

                    //categories.set(temp_categories);
                    //process_nested_categories(get(categories));
                }
            });
        }
    }

    if (url.searchParams.has('zoek')) {
        _query = url.searchParams.get('zoek', '');
        // Clear the (current_)dossier_id, because searching happens across all
        // documents instead of only the documents in a dossier
        dossier_id = '';
        current_dossier_id.set('');
    }

    query.set(_query);

    let date_range = {};
    if (get(start_date_string)) {
        date_range['from'] = get(start_date_string).replace(/T.*Z/, 'T00:00:00+02:00');
    }
    if (get(end_date_string)) {
        date_range['to'] = get(end_date_string).replace(/T.*Z/, 'T23:59:59+02:00');
    }

    // The filters for the API call
    let filters = {};

    // If any categories are checked by the user, then add those as filter;
    // don't add category filters if a dossier is specified, because the API
    // knows which categories belong to it
    if (browser && get(categories) && (!dossier_id || dossier_id === 'all')) {
        // Get all categories that are checked
        let filtered_categories = Object.keys(get(categories)).filter(function(category) {
            return get(categories)[category];
        });

        if (filtered_categories.length) {
            filters['categories'] = filtered_categories;
        }
    }

    // By default only get agenda items starting from today
    let date_range_agenda = date_range;
    if (!date_range?.from) {
        // This actually generates a UTC date, so instead of at midnight the
        // user will be shown agenda items for the new day at 01:00 or 02:00
        // depending on summer time (if you change this make sure that the new
        // method generates the same time on the server as on in a CE(S)T
        // browser, otherwise this will result in extra call around midnight
        // because the ssr data_range_agenda value will be different from the
        // csr value and thus resulting in extra fetch calls)
        date_range_agenda = {from: new Date().toISOString().slice(0, -13) + '00:00:00+01:00'};
    }

    return [_query, dossier_id, date_range, date_range_agenda, filters];
}

// If a dossier is selected this will get and set the categories associated
// with that dossier
export function process_dossier() {
    if (get(current_dossier_id)) {
        let temp_categories = get(categories);

        // Check the categories for the selected dossier
        if (get(current_dossier_id) !== 'all') {
            uncheck_categories(categories);

            get(dossiers).forEach(dossier => {
                if (dossier.id == get(current_dossier_id)) {
                    dossier.notification_item_category_filter.forEach(category => {
                        temp_categories[category] = true;
                    });

                    categories.set(temp_categories);
                    process_nested_categories(get(categories));
                }
            });
        }
    }
}

// Used when logging in and out to clear state
export function clear_state() {
    uncheck_categories(categories);
    start_date.set(null);
    start_date_string.set(null);
    end_date.set(null);
    end_date_string.set(null);
    previous_query.set('a');
    current_dossier_id.set('');
    query.set('');
}

// Clear any previous 'clicked_*' states
export function clear_clicked_cat_and_date() {
    clicked_category.set(false);
    clicked_date.set(false);
}

export function totalString(total) {
    let totalStr = Intl.NumberFormat('nl-NL').format(total)
    if (total == 10000) totalStr = totalStr + '+';
    return totalStr;
}